import { FormikProvider, useFormik } from 'formik';
import React, { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import { Tooltip } from 'shared/hooks/use-tooltips-for';
import { TaskName } from 'shared/ui';
import { ItemActionModal } from 'shared/ui/ItemActionModal';

import { audioApi, pdfApi, taskApi, videoApi, wordApi } from '../../../api';
import { imgApi } from '../../../api/img-api';
import plusSvg from '../../../assets/img/add-circle.svg';
import trashSvg from '../../../assets/img/trash.svg';
import { TaskWithLinkType, WorkType, TaskKeysType, ExcludeTask as ExcludesTaskType } from '../../../shared/types';
import { excludesTaskSchema } from '../../../shared/types/task';
import Clue from '../../../shared/ui/Clue';
import TaskWrapper from '../TaskWrapper';
import InputPoint from 'Components/InputPoint/InputPoint';

export type ExcludesTaskProps = ExcludesTaskType & {
  handleDelete: (id: string) => void;
  userType: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  currentICLId: string;
  updateICL: boolean;
  workType?: WorkType;
  index: number;
  points?: number;
};

function ExcludesTask({
  id,
  handleDelete,
  completedAt,
  text,
  handleChangeTaskByKey,
  hasAnswer,
  hasStar,
  indexNumber,
  isActive,
  createdAt,
  type,
  currentICLId,
  updateICL,
  hasPhoto,
  deadline,
  audioUrl,
  videoUrl,
  pdfUrl,
  wordUrl,
  taskPhotos,
  answersPhotos,
  editedAnswerPhotos,
  workType,
  studentPdfUrl,
  studentWordUrl,
  options,
  index,
}: ExcludesTaskProps) {
  const { t } = useTranslation();
  const [taskId, setTaskId] = useState<string>('');
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [isRemoveWordModalOpen, setIsRemoveWordModalOpen] = useState<{ lineId: string; word?: string } | null>(null);
  const isExam = workType === WorkType.exam;

  useEffect(() => {
    const init = async () => {
      try {
        const task = await taskApi.getTask(currentICLId, id);

        if (task) {
          setTaskId(task.id);
        }
      } catch (error) {
        console.log(error);
      }
    };

    init(); // to check if task exists in Firestore
  }, []);

  const initialFormValues = {
    text: updateICL ? text.replace(/<br>/g, '\n') : '',
    id,
    indexNumber,
    isActive,
    createdAt,
    type,
    hasAnswer,
    hasStar,
    hasPhoto,
    options,
  };

  const formValues = isExam ? { ...initialFormValues, points: 0 } : initialFormValues;

  const formik = useFormik({
    initialValues: formValues,

    onSubmit: async values => {
      const textWithLB = values.text.replace(/\n/g, '<br>');

      if (!taskId) {
        await taskApi
          .addTask<ExcludesTaskType>(currentICLId, { ...values, indexNumber, text: textWithLB, completedAt })
          .then(id => {
            setTaskId(id);
            toast.success(t('Successfully added!'));

            return id;
          });
      } else {
        await taskApi.updateTask<ExcludesTaskType>(currentICLId, {
          ...values,
          text: textWithLB,
          completedAt,
        });
        toast.success(t('Successfully edited!'));
      }
    },
    validationSchema: excludesTaskSchema,
  });

  const handleChangeTaskByKeyWithFormik = (id: string, key: TaskKeysType, value: string | boolean | string[]) => {
    handleChangeTaskByKey(id, key, value);

    formik.setFieldValue(key, value);
  };

  const handleAddLine = () => {
    formik.validateForm().then(errors => {
      if (errors.options && Array.isArray(errors.options)) return;

      const id = uuidv4();

      const newOptions = formik.values.options;
      newOptions.push({ id, words: [], excludeWords: [] });

      formik.setFieldValue('options', newOptions);
    });
  };

  const handleAddWord = (lineId: string, word: string) => {
    const newOptions = formik.values.options.map(option => {
      if (option.id === lineId) {
        const newWords = option.words;

        if (newWords.includes(word)) {
          toast.error(t('This word is already added!'));
          console.log('The word is already added');
        } else {
          newWords.push(word);
          return { ...option, words: newWords };
        }
      }

      return option;
    });

    formik.setFieldValue('options', newOptions);
  };

  const handleDeleteWord = (lineId: string, word: string) => {
    const newOptions = formik.values.options.map(option => {
      if (option.id === lineId) {
        const newWords = option.words.filter(w => w !== word);

        return { ...option, words: newWords };
      }

      return option;
    });

    formik.setFieldValue('options', newOptions);
  };

  const handleDeleteLine = (lineId: string) => {
    const newOptions = formik.values.options.filter(option => option.id !== lineId);

    formik.setFieldValue('options', newOptions);
  };

  const handleExcludeWord = (lineId: string, word: string, isWordAlreadyExcluded: boolean) => {
    const newOptions = formik.values.options.map(option => {
      if (isWordAlreadyExcluded) {
        const newExcludeWords = option.excludeWords.filter(w => w !== word);

        return { ...option, excludeWords: newExcludeWords };
      } else {
        if (option.id === lineId) {
          const newExcludeWords = option.excludeWords;
          newExcludeWords.push(word);

          return { ...option, excludeWords: newExcludeWords };
        }
      }
      return option;
    });

    formik.setFieldValue('options', newOptions);
  };

  const deleteTask = async () => {
    if (!taskId) {
      handleDelete(id);
    }

    if (updateICL || taskId) {
      handleDelete(id);

      setIsRemovingLoading(true);
      wordUrl && (await wordApi.removeWord(wordUrl, currentICLId, id));
      pdfUrl && (await pdfApi.removePdf(pdfUrl, currentICLId, id));
      studentWordUrl && (await wordApi.removeWord(studentWordUrl, currentICLId, id, true));
      studentPdfUrl && (await pdfApi.removePdf(studentPdfUrl, currentICLId, id, true));
      videoUrl && (await videoApi.removeVideo(videoUrl, currentICLId, id));
      audioUrl && (await audioApi.removeAudio(audioUrl, currentICLId, id));
      taskPhotos &&
        taskPhotos.forEach(async photo => await imgApi.removeImg(photo, currentICLId, id, taskPhotos, 'taskPhotos'));
      answersPhotos &&
        answersPhotos.forEach(
          async photo => await imgApi.removeImg(photo, currentICLId, id, answersPhotos, 'answersPhotos'),
        );
      editedAnswerPhotos &&
        editedAnswerPhotos.forEach(
          async photo => await imgApi.removeImg(photo, currentICLId, id, editedAnswerPhotos, 'editedAnswerPhotos'),
        );
      await taskApi.removeTask<TaskWithLinkType>(currentICLId, id);
      setIsRemovingLoading(false);
    }
  };

  const renderOptions = (wordArr: string[], excludeWords: string[], lineId: string) => {
    return wordArr.map((word, index) => {
      return (
        <div key={index} className="mb-[10px] flex items-center gap-[10px] rounded-[10px] bg-[#DCF0FF] p-[10px]">
          <Input
            type="checkbox"
            className="mt-0 h-[20px] w-[20px] shrink-0 cursor-pointer border-2 border-[#4383F2] checked:border-[#4383F2] checked:bg-[#4383F2] checked:ring-[#4383F2] checked:ring-opacity-50 focus:border-[#4383F2] focus:ring-[#4383F2] focus:ring-opacity-50"
            onChange={e => handleExcludeWord(lineId, word, excludeWords.includes(word))}
            checked={excludeWords.includes(word)}
          />

          <div>{word}</div>

          <Tooltip title={'delete'}>
            <button className="shrink-0" onClick={() => setIsRemoveWordModalOpen({ lineId, word: word })} type="button">
              <img src={trashSvg} />
            </button>
          </Tooltip>
        </div>
      );
    });
  };

  const renderOptionsLines = () => {
    return (
      <>
        {formik.values.options.map(({ words, excludeWords, id }, index) => {
          return (
            <div key={id}>
              <div className="!my-3 font-bold">
                {t('wordLine')} №{index + 1}.
              </div>

              {formik.errors?.options?.[index]?.words && typeof formik.errors?.options?.[index]?.words === 'string' && (
                <p className="h-fit text-sm font-medium text-red-600 lg:top-[55px]">
                  {t(formik.errors?.options?.[index]?.words)}
                </p>
              )}

              <div className="mb-[20px] flex flex-wrap items-baseline">
                <div className="mr-[10px] flex flex-wrap gap-[10px]">{renderOptions(words, excludeWords, id)}</div>

                <div className="flex items-start gap-[15px]">
                  <input
                    type={'text'}
                    value={inputValues[id] || ''}
                    className="h-[44px] !rounded-[8px] !border-[1px] !border-[#52C7FA] px-2"
                    onChange={e => setInputValues({ ...inputValues, [id]: e.target.value })}
                  />

                  <Tooltip title="add word option">
                    <button
                      className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
                      onClick={() => {
                        handleAddWord(id, inputValues[id]);
                        setInputValues({ ...inputValues, [id]: '' });
                      }}
                      disabled={!inputValues[id]}
                    >
                      <img src={plusSvg} alt="plus word" />
                    </button>
                  </Tooltip>
                  <Tooltip title="delete task line">
                    <button
                      className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600"
                      onClick={() => setIsRemoveWordModalOpen({ lineId: id })}
                    >
                      <img src={trashSvg} alt="mic" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          );
        })}
        <ItemActionModal
          isOpen={!!isRemoveWordModalOpen}
          close={() => setIsRemoveWordModalOpen(null)}
          title={isRemoveWordModalOpen?.word ? 'deleteTaskOption' : 'deleteTaskLine'}
          onHandle={() => {
            if (isRemoveWordModalOpen?.word) handleDeleteWord(isRemoveWordModalOpen.lineId, isRemoveWordModalOpen.word);
            else if (isRemoveWordModalOpen?.lineId) handleDeleteLine(isRemoveWordModalOpen.lineId);
            setIsRemoveWordModalOpen(null);
          }}
          warning={isRemoveWordModalOpen?.word ? 'deleteOptionWarning' : 'deleteLineWarning'}
        />
      </>
    );
  };

  return (
    <TaskWrapper
      index={index}
      id={id}
      handleDelete={deleteTask}
      completedAt={completedAt}
      handleChangeTaskByKey={handleChangeTaskByKeyWithFormik}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      save={formik.submitForm}
      formikProp={formik}
      updateICL={updateICL}
      isDeleteButtonDisabled={isRemovingLoading}
      hasPhoto={hasPhoto}
      deadline={deadline}
      isActive={isActive}
      currentICLId={currentICLId}
      audioUrl={audioUrl}
      remoteTaskId={taskId}
      videoUrl={videoUrl}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      taskPhotos={taskPhotos}
      workType={workType}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      inputValues={inputValues}
    >
      <FormikProvider value={formik}>
        <div className="!mb-4 lg:!mb-5">
          <TaskName />
        </div>

        {isExam && (
          <InputPoint value={formik.values.points || 0} onChange={value => formik.setFieldValue('points', value)} />
        )}

        <div className="relative !mb-4 lg:!mb-5">
          <div className="text-[14px] font-bold uppercase lg:text-[18px]">{t('exercise heading')}:</div>

          <textarea
            value={formik.values.text}
            className={
              formik.errors.text
                ? 'h-[83px] w-full rounded-[20px] border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#FA6238_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
                : 'h-[83px] w-full rounded-[20px] border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
            }
            onChange={event => formik.setFieldValue('text', event.target.value)}
            placeholder={t('enter task title')}
          />

          {!!formik.errors.text && (
            <p className="absolute left-0 top-14 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:top-[60px]">
              {t(formik.errors.text)}
            </p>
          )}
        </div>

        <div className="relative !my-4 lg:!my-5">
          <Clue text={'ecxludeTaskClue'} />

          {formik.errors?.options && typeof formik.errors?.options === 'string' && (
            <p className="!mb-0 mt-4 h-fit text-sm font-medium text-red-600 lg:top-[55px]">
              {t(formik.errors?.options)}
            </p>
          )}

          <div>{renderOptionsLines()}</div>

          <button
            type="button"
            onClick={() => handleAddLine()}
            disabled={Array.isArray(formik.errors?.options) && formik.errors?.options?.length > 0}
            className="btn btn-primary moreStatic mt-4 !px-[50px] lg:w-[240px]"
          >
            + {t('add line')}
          </button>
        </div>
      </FormikProvider>
    </TaskWrapper>
  );
}

export default memo(ExcludesTask);
